import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection.jsx";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast.js";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import AddButton from "../../../components/CustomComponents/Buttons/AddButton/AddButton.jsx";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate, EMPTY_GRID_PREFERENCES } from "../../../utils/preferencesUtils";
import useResetPageOnFilterChange from "../../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../../utils/catalogTotalRecords";


import ModernGridToolbar from "../../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import StatusBadge from "../../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import TableQrModal from "../../../components/CustomComponents/TableQr/TableQrModal";
import TableActionsMenu, {
  TableActionsItem,
} from "../../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const DINING_AREA_SORT_FIELDS = {
  name: "sA_Name",
  sortOrder: "sA_SortOrder",
  id: "id",
};

const DINING_AREA_SORT_LABELS = {
  [DINING_AREA_SORT_FIELDS.name]: "Name",
  [DINING_AREA_SORT_FIELDS.sortOrder]: "Order By",
  [DINING_AREA_SORT_FIELDS.id]: "ID",
};

const DEFAULT_VISIBLE_COLUMNS = {
  status: true,
  Tables: false,
  Seats: false,
};

const DEFAULT_SORT_ORDER = { value: "ASC", label: "ASC" };
const DEFAULT_SORT_BY = { value: "sA_SortOrder", label: "Order By" };
const DEFAULT_PAGE_SIZE = { value: "50", label: "50" };
const VALID_PAGE_SIZES = ["20", "50", "100"];
const PAGE_KEY = "diningAreas";

const ManageDiningAreas = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusConfirmArea, setStatusConfirmArea] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrServiceArea, setQrServiceArea] = useState(null);
  const [qrAutoBrowserPrint, setQrAutoBrowserPrint] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const areasFetchSeqRef = useRef(0);
  const pageKey = PAGE_KEY;
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] ?? EMPTY_GRID_PREFERENCES
  );
  const visibleColumns = useMemo(
    () => mergeVisibleColumns(preferences.visibleColumns, DEFAULT_VISIBLE_COLUMNS),
    [preferences.visibleColumns]
  );
  const sortOrder = preferences.sortOrder || DEFAULT_SORT_ORDER;
  const sortBy = preferences.sortBy || DEFAULT_SORT_BY;
  const pageSize =
    preferences.pageSize &&
    VALID_PAGE_SIZES.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : DEFAULT_PAGE_SIZE;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DINING_AREA_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const hasFiltersApplied = () => search.trim() !== "";

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: DEFAULT_VISIBLE_COLUMNS }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, DEFAULT_VISIBLE_COLUMNS)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, DEFAULT_VISIBLE_COLUMNS);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: DEFAULT_SORT_ORDER }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: DEFAULT_SORT_BY }));
    }
    if (
      !preferences.pageSize ||
      !VALID_PAGE_SIZES.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: DEFAULT_PAGE_SIZE }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const fetchItemsData = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++areasFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/api/ServiceArea/GetAllServiceAreas",
        {
          params: {
            CompID,
            Search: search?.trim() || "",
          },
        }
      );

      if (fetchSeq !== areasFetchSeqRef.current) return;

      const itemsData = Array.isArray(response.data.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      setItems(itemsData);
      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, itemsData.length);
      }
    } catch (err) {
      if (fetchSeq !== areasFetchSeqRef.current) return;
      showErrorToast("Failed to load service areas");
      setItems([]);
    } finally {
      if (fetchSeq === areasFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, CompID]);

  const sortedItems = useMemo(() => {
    const list = [...items];
    const field = sortBy?.value || DINING_AREA_SORT_FIELDS.sortOrder;
    const dir = sortOrder?.value === "ASC" ? 1 : -1;
    list.sort((a, b) => {
      const av = a[field] ?? a[field === "id" ? "id" : field];
      const bv = b[field] ?? b[field === "id" ? "id" : field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv) * dir;
      }
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
    return list;
  }, [items, sortBy?.value, sortOrder?.value]);

  const totalRecords = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedItems = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return sortedItems.slice(start, start + size);
  }, [sortedItems, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(areasFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchItemsData(), [fetchItemsData]);

  // Client-side sort only — do not toggle loading on sort (nothing re-fetches).
  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No service areas match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no service areas to show right now.";
  };

  const handleAddItemClick = () => {
    navigate("/diningAreas/addDiningArea");
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (items[i]?.id) {
            newSelected.add(items[i].id);
          }
        }
      } else {
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(items.map((item) => item.id).filter(Boolean));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for deletion.");
      return;
    }

    if (items.length - selectedItems.size < 1) {
      showErrorToast("At least one service area is required.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected service areas?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete("/api/ServiceArea/DeleteServiceArea", {
            params: { ID: itemId, CompID },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      await fetchItemsData();
      showSuccessToast(`${selectedItems.size} service areas deleted successfully.`);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected service areas.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for editing.");
      return;
    }

    const selectedItemIds = Array.from(selectedItems);
    navigate(`/diningAreas/bulkEdit`, { state: { selectedItemIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for status change.");
      return;
    }

    const previousItems = [...items];
    try {
      setLoading(true);
      setLoading(true);

      const updatedItems = items.map((item) =>
        selectedItems.has(item.id)
          ? { ...item, sA_Status: publishStatus }
          : item
      );
      setItems(updatedItems);

      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.post("/api/ServiceArea/UpdateServiceAreaStatus", {
            CompID,
            ID: itemId,
            SA_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} service areas status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected service areas status.");
      setItems(previousItems);
      await fetchItemsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (items.length === 1) {
      showErrorToast("At least one service area is required.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${item.sA_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(
        "/api/ServiceArea/DeleteServiceArea",
        {
          params: { ID: item.id, CompID },
        }
      );

      if (response.data.status) {
        setItems((prevItems) => prevItems.filter((i) => i.id !== item.id));
        showSuccessToast("Service area deleted successfully");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      // Error in handleDeleteItem silently
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newItems = [...sortedItems];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(overIndex, 0, draggedItem);

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sA_SortOrder: index + 1,
    }));

    try {
      setLoading(true);
      const payload = updatedItems.map((item) => ({
        ID: item.id,
        SA_SortOrder: item.sA_SortOrder,
        CompID,
      }));

      const response = await axiosInstance.post(
        "/api/ServiceArea/UpdateServiceAreaSortOrder",
        payload
      );

      if (response.data.status) {
        setItems(updatedItems);
        showSuccessToast("Service areas reordered successfully");
      } else {
        showErrorToast(
          response.data.message || "Failed to update service areas order"
        );
      }
    } catch (error) {
      // Error updating item order silently
      showErrorToast("Failed to update service areas order");
    } finally {
      dragItem.current = null;
      dragOverItem.current = null;
      setLoading(false);
    }
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleEditArea = (item) => {
    if (!item?.id) return;
    navigate(`/diningAreas/updateDiningArea/${item.id}`);
  };

  const handleOpenAreaQrCodes = (item, { autoBrowserPrint = false } = {}) => {
    if (!item?.id) {
      showErrorToast("Save this service area before generating QR codes");
      return;
    }
    setQrServiceArea({
      id: item.id,
      name: item.sA_Name || "",
    });
    setQrAutoBrowserPrint(!!autoBrowserPrint);
    setQrModalOpen(true);
  };

  const confirmAreaStatusChange = async () => {
    if (!statusConfirmArea?.id) return;

    const { id, sA_Status, sA_Name } = statusConfirmArea;
    const newStatus = !sA_Status;
    const previousItems = [...items];

    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, sA_Status: newStatus } : item
      )
    );
    setStatusConfirmArea(null);

    try {
      const response = await axiosInstance.post(
        "/api/ServiceArea/UpdateServiceAreaStatus",
        {
          CompID,
          ID: id,
          SA_Publish: newStatus,
        }
      );

      if (!response.data.status) {
        showErrorToast(
          response.data.message || "Failed to update service area status"
        );
        setItems(previousItems);
        return;
      }
      showSuccessToast(
        `"${sA_Name || "Service area"}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      showErrorToast("Failed to update service area status");
      setItems(previousItems);
    }
  };

  const renderNoDataMessage = () => {
    if (loading || sortedItems.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No service areas found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Service Area</li>
          </ol>
          <h1 className="page-header mb-0">Manage Service Area</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddItemClick}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Area
          </button>
        </div>
      </div>

      {showEmptyCatalogState && (
        <AddButton onClick={handleAddItemClick} title="Add Area" />
      )}

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Service Areas"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by area name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) => dispatch(setVisibleColumns({ pageKey, columns: cols }))}
                defaultColumns={DEFAULT_VISIBLE_COLUMNS}
                pageKey={pageKey}
              />
            }
          >
            <div className="px-3 pb-3">
                {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} area{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Areas"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleBulkStatusChange(true)}
                          title="Publish Selected Areas"
                        >
                          <i className="fa fa-check-circle me-1"></i>
                          Publish
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleBulkStatusChange(false)}
                          title="Unpublish Selected Areas"
                        >
                          <i className="fa fa-times-circle me-1"></i>
                          Unpublish
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Areas"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}
                {!loading && pagedItems.length > 0 && (
                  <div
                    className="table-responsive mt-3 dining-area-table-wrap"
                    style={{ minHeight: "200px", overflow: "visible" }}
                  >
                    <table className="table table-hover" style={{ "--modern-grid-sort-active": themeColor }}>
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  items.length > 0 &&
                                  selectedItems.size === items.length
                                }
                              />
                            </th>
                          )}
                          <th className="pt-2 pb-2"></th>
                          <SortableColumnHeader sortField={DINING_AREA_SORT_FIELDS.name} sortBy={sortBy} sortOrder={sortOrder} onSort={handleColumnSort}>Area Name</SortableColumnHeader>
                          {visibleColumns.Tables && (
                            <th className="pt-2 pb-2 text-center">Tables</th>
                          )}
                          {visibleColumns.Seats && (
                            <th className="pt-2 pb-2 text-center">Seats</th>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedItems.map((item, index) => (
                          <tr
                            key={item.id || index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, (pageNumber - 1) * Number(pageSize.value) + index)}
                            onDragEnter={(e) => handleDragEnter(e, (pageNumber - 1) * Number(pageSize.value) + index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(item.id)}
                                  onChange={(e) =>
                                    handleSelectItem(item.id, index, e)
                                  }
                                  disabled={!item.id}
                                  data-chkbox-shiftsel="type1"
                                />
                              </td>
                            )}
                            <td className="align-middle">
                              <i
                                className="fa fa-bars text-secondary"
                                style={{ cursor: "grab" }}
                              ></i>
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => item.id && handleEditArea(item)}
                              style={{ cursor: item.id ? "pointer" : "default" }}
                            >
                              {item.sA_Name || "N/A"}
                            </td>
                            {visibleColumns.Tables && (
                              <td
                                className="align-middle text-center"
                                onClick={() => item.id && handleEditArea(item)}
                                style={{ cursor: item.id ? "pointer" : "default" }}
                              >
                                {item.numberofTables || "N/A"}
                              </td>
                            )}
                            {visibleColumns.Seats && (
                              <td
                                className="align-middle text-center"
                                onClick={() => item.id && handleEditArea(item)}
                                style={{ cursor: item.id ? "pointer" : "default" }}
                              >
                                {item.numberofSeats || "N/A"}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {item.id ? (
                                  <StatusBadge
                                    isActive={!!item.sA_Status}
                                    onClick={() => setStatusConfirmArea(item)}
                                  />
                                ) : (
                                  <StatusBadge isActive={!!item.sA_Status} disabled />
                                )}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu
                                menuEnd
                                menuStyle={{ zIndex: 1060 }}
                                disabled={!item.id}
                              >
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => handleEditArea(item)}
                                  disabled={!item.id}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  icon="fa fa-qrcode"
                                  onClick={() => handleOpenAreaQrCodes(item)}
                                  disabled={!item.id}
                                  title="Download QR codes for published tables"
                                >
                                  Download QR codes
                                </TableActionsItem>
                                <TableActionsItem
                                  icon="fa fa-print"
                                  onClick={() =>
                                    handleOpenAreaQrCodes(item, {
                                      autoBrowserPrint: true,
                                    })
                                  }
                                  disabled={!item.id}
                                  title="Print QR codes for published tables"
                                >
                                  Print QR codes
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteItem(item)}
                                  disabled={!item.id}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <ModernGridPagerFooter pageSize={pageSize} onPageSizeChange={handlePageSizeChange} totalRecords={totalRecords} pageNumber={pageNumber} totalPages={totalPages} setPageNumber={setPageNumber} />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change service area status?"
        subtitle={
          statusConfirmArea
            ? `Are you sure you want to set "${statusConfirmArea.sA_Name || "this area"}" to ${
                statusConfirmArea.sA_Status ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmArea(null)}
        isOpen={!!statusConfirmArea}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmAreaStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />

      <TableQrModal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setQrServiceArea(null);
          setQrAutoBrowserPrint(false);
        }}
        CompID={CompID}
        tableId={null}
        serviceAreaId={qrServiceArea?.id || null}
        serviceAreaName={qrServiceArea?.name || ""}
        autoBrowserPrint={qrAutoBrowserPrint}
      />
    </div>
  );
};

export default ManageDiningAreas;